import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";

import * as Api from "@/api";
import { setSystemSettingsPage } from "@/store/reducers/app";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useInterval from "@/utils/hooks/useInterval";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, { viGear, viPerson } from "@sippy-platform/valkyrie";

import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { collection, getFirestore, onSnapshot } from "@firebase/firestore";
import {
  Box,
  Button,
  Card,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";

import UserCard from "./useractivity/UserCard";

export default function UserActivity() {
  const dispatch = useDispatch();

  const pbx = useSelector((state) => state.domain.pbx);
  const dashboardSettings = useSelector((state) => state.dashboard.find((_pbx) => _pbx.context === pbx.context));
  const userStore = useSelector((state) => state.domain.users);
  const searchValue = useSelector((state) => state.ui.searchValue);

  const [loadedContext, setLoadedContext] = useState(null);
  const [activeUserList, setActiveUserList] = useState([]);

  const loading = loadedContext !== pbx?.context;

  // Fetch data and keep it clean
  useEffect(() => {
    let cancelled = false;

    Promise.all([Api.getActiveUsers(), Api.getUsers()])
      .then((response) => {
        if (!cancelled) setActiveUserList(response[0].data.sippeers);
      })
      .finally(() => {
        if (!cancelled) setLoadedContext(pbx?.context);
      });

    return () => {
      cancelled = true;
    };
  }, [pbx?.context]);

  useInterval(() => {
    Promise.all([Api.getActiveUsers(), Api.getUsers()]).then((response) => {
      setActiveUserList(response[0].data.sippeers);
    });
  }, dashboardSettings.settings.refreshDelay * 60000);

  // Generate visible users
  const dashboardUserList = useMemo(() => {
    let list = userStore.list;

    if (!dashboardSettings.settings.showAllUsers) {
      list = userStore.list.filter((user) => dashboardSettings.userList.includes(user.id));
    }

    if (dashboardSettings.settings.filterByStatus === "active") {
      list = list.filter((user) => activeUserList.find((_active) => _active.username === user.username));
    } else if (dashboardSettings.settings.filterByStatus === "inactive") {
      list = list.filter((user) => activeUserList.findIndex((_active) => _active.username === user.username) === -1);
    }

    return list;
  }, [
    activeUserList,
    dashboardSettings.settings.filterByStatus,
    dashboardSettings.settings.showAllUsers,
    dashboardSettings.userList,
    userStore.list,
  ]);

  // Search
  const searchResults = useSearch(dashboardUserList, searchValue, ["name", "exten"]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  // Keep track of calls
  const [allCalls, setAllCalls] = useState([]);
  const [busyCalls, setBusyCalls] = useState([]);

  useEffect(() => {
    let unsubscribeActivity;

    const auth = getAuth();
    let unsubscribeUser = onAuthStateChanged(auth, (user) => {
      if (user) {
        let db = getFirestore();
        unsubscribeActivity = onSnapshot(
          collection(db, `states/${pbx.context}/dialogs`),
          (docs) => {
            const busyUsers = [];
            const docCalls = [];

            docs.forEach((doc) => {
              const data = doc.data();
              docCalls.push(data);
              if (data.callee.includes(pbx.context)) busyUsers.push(data.callee);
              if (data.caller.includes(pbx.context)) busyUsers.push(data.caller);
            });

            setAllCalls(docCalls);
            setBusyCalls(busyUsers);
          },
          (error) => {
            console.log(error.toString());
          },
        );
      }
    });

    return () => {
      unsubscribeUser();

      if (unsubscribeActivity) {
        unsubscribeActivity();
      }
    };
  }, [pbx?.context]);

  // User lists
  const userCategories = useMemo(() => {
    if (loading) {
      if (dashboardSettings.settings.groupByStatus) {
        return [
          { title: "Busy", list: [...Array(4)] },
          { title: "Available", list: [...Array(9)] },
          { title: "Unavailable", list: [...Array(13)] },
        ];
      } else {
        return [{ title: "Users", list: [...Array(26)] }];
      }
    }

    if (searchResults.length === 0) {
      return [];
    }

    if (dashboardSettings.settings.groupByStatus) {
      const busyUsers = searchResults.filter((user) => busyCalls.indexOf(user.username) >= 0);
      const availableUsers = searchResults
        .filter((user) => activeUserList.find((activeUser) => activeUser.username === user.username))
        .filter((user) => busyCalls.indexOf(user.username) === -1);
      const unavailableUsers = searchResults.filter(
        (user) =>
          !busyUsers.find((_user) => _user.username === user.username) &&
          !availableUsers.find((_user) => _user.username === user.username),
      );

      return [
        { title: "Busy", list: busyUsers },
        { title: "Available", list: availableUsers },
        { title: "Unavailable", list: unavailableUsers },
      ];
    } else {
      return [{ title: "Users", list: searchResults }];
    }
  }, [loading, searchResults, dashboardSettings.settings.groupByStatus, busyCalls, activeUserList]);

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      {loading ? (
        userCategories.map((category) => {
          if (category.list.length >= 1) {
            return (
              <Fragment key={category.title}>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    height: 40,
                  }}
                >
                  <Typography variant="h5" component="h3">
                    {category.title}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                    gridAutoRows: 24,
                    gap: 1,
                  }}
                >
                  {category.list.map((i, key) => (
                    <Card key={key} sx={{ gridRow: `2 span` }}>
                      <List>
                        <ListItem
                          disablePadding
                          secondaryAction={<Skeleton variant="circular" width={24} height={24} />}
                        >
                          <ListItemButton disabled>
                            <ListItemAvatar>
                              <Skeleton variant="circular" width={32} height={32} />
                            </ListItemAvatar>
                            <ListItemText
                              disableTypography
                              primary={<Skeleton height={18} width="80%" />}
                              secondary={<Skeleton height={12} width="40%" />}
                            />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </Card>
                  ))}
                </Box>
              </Fragment>
            );
          }
        })
      ) : dashboardUserList.length ? (
        userCategories.map((category) => {
          if (category.list.length >= 1) {
            return (
              <Fragment key={category.title}>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    height: 40,
                  }}
                >
                  <Typography variant="h5" component="h3">
                    {category.title}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                    gridAutoRows: 24,
                    gap: 1,
                  }}
                >
                  {category.list.map((user) => (
                    <UserCard key={user.id} user={user} activeUserList={activeUserList} allCalls={allCalls} />
                  ))}
                </Box>
              </Fragment>
            );
          }
        })
      ) : (
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              height: 40,
            }}
          >
            <Typography variant="h5" component="h3">
              Users
            </Typography>
          </Stack>
          <Typography>Set up users to show on the dashboard.</Typography>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => dispatch(setSystemSettingsPage("dashboard"))}
              variant="contained"
              startIcon={<Valkyrie icon={viGear} />}
            >
              Manage dashboard users
            </Button>
            <Button
              component={NavLink}
              to={"../call-flow/users"}
              variant="contained"
              startIcon={<Valkyrie icon={viPerson} />}
            >
              Users
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
